import { notFound } from "@tanstack/react-router";
import { gamePresentationOf } from "@softmax/web-features/coworlds";
import { matchRoute } from "@softmax/web-addresses";
import { requireGameResource } from "./data/context";
import { readPitchPage } from "@/features/desk/records";
import { deskFor } from "@/features/desk/records";
import { readCreatorUpload } from "@/features/coworlds/data/index";
import { readFrontDoor } from "@/features/preview/data/index";

import { gameTheaterQueryKeys } from "./game-types";
import { readGameTheater } from "./game-theater";

export async function readGameIndex({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}) {
  const url = new URL(href, window.location.href);
  const search: Record<string, string | readonly string[] | undefined> = {};

  for (const key of new Set(url.searchParams.keys())) {
    const values = url.searchParams.getAll(key);
    search[key] = values.length === 1 ? values[0] : values;
  }

  const route = matchRoute(url.pathname);

  if (!["game.home", "game.league", "game.desk"].includes(route.name))
    throw notFound();

  const game = await requireGameResource({ signal, href });

  if (
    route.name === "game.desk" ||
    (game.mode === "league" && game.presentation === "desk")
  ) {
    const spec = deskFor(game.coworldName);

    if (!spec || !game.league || !game.coworldName) throw notFound();

    const pitches = await readPitchPage({
      signal,
      leagueId: game.league.id,
      cursor: null,
    });
    return { kind: "desk" as const, game, pitches, spec };
  }

  if (game.mode === "upload") {
    const data = await readCreatorUpload({
      signal,
      name: game.coworldName,
      id: game.coworldId,
      unlisted: game.address.catalog_status === "unlisted",
    });
    return { kind: "upload" as const, game, data };
  }

  if (
    game.league?.requestedSlug === null &&
    !gameTheaterQueryKeys.some((key) => url.searchParams.has(key)) &&
    gamePresentationOf(game.corpusSlug).home === "sugarscape"
  )
    return { kind: "sugarscape" as const, game };

  if (game.presentation === "home") {
    const data = await readFrontDoor({ signal, context: game, search });

    if (!data) throw notFound();

    return { kind: "home" as const, game, data };
  }

  const data = await readGameTheater({
    signal,
    href,
    game,
  });
  return { kind: "theater" as const, game, data };
}

export type GameIndexPage = Awaited<ReturnType<typeof readGameIndex>>;
